import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import CarCard from '../components/CarCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Plus, Car, Eye, LayoutDashboard, Settings, User, ChevronRight,
  Trash2, Camera, MapPin, Phone, Globe, Instagram, Facebook,
  Twitter, Upload, FileText, Key, Download, Search, Edit3,
  Zap, Heart, MessageCircle, Send, X, Check, AlertCircle, Loader2,
  ChevronLeft, Package, Menu, LogOut, ExternalLink, Shield, Bookmark
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../LanguageContext';
import ErrorBoundary from '../components/ErrorBoundary';

const SECTIONS = [
  { id: 'overview', tk: 'dash.overview', icon: LayoutDashboard },
  { id: 'cars', tk: 'dash.myCars', icon: Car },
  { id: 'add-car', tk: 'dash.addCar', icon: Zap },
  { id: 'bulk-import', tk: 'dash.bulkImport', icon: Package },
  { id: 'favorites', tk: 'dash.favorites', icon: Heart },
  { id: 'saved-searches', tk: 'dash.savedSearches', icon: Search },
  { id: 'messages', tk: 'dash.messages', icon: MessageCircle },
  { id: 'profile', tk: 'dash.profile', icon: User },
  { id: 'my-profile', tk: 'nav.profile', icon: ExternalLink, fallback: 'Public Profile' },
  { id: 'settings', tk: 'dash.settings', icon: Settings },
];

// Hidden sections (no sidebar link, accessed via /dashboard/edit-car/:carId)
const HIDDEN_SECTIONS = ['edit-car'];

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const { t } = useTranslation();
  const label = (sec) => t(sec.tk) || sec.fallback || sec.tk;

  // Extract section from URL: /dashboard/edit-car/abc → "edit-car/abc"
  const pathAfterDash = location.pathname.replace('/dashboard', '').replace(/^\//, '');
  const activeSection = pathAfterDash || 'overview';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const backendBase = API.replace('/api', '');

  const setSection = (s) => {
    if (s === 'my-profile') {
      navigate(`/profile/${user?.id}`);
      return;
    }
    navigate(`/dashboard/${s}`);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Lock body scroll on mobile when sidebar open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  return (
    <div className="h-screen flex overflow-hidden bg-[#030810]">
      {/* ===== SIDEBAR (Desktop: always visible, Mobile: overlay) ===== */}

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar panel */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-[#060a14] border-r border-white/5 flex flex-col
        transition-transform duration-300 ease-in-out
        md:relative md:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Sidebar Header - Logo */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-white/5 shrink-0">
          <Link to="/" className="flex items-center">
            <img src="/logo-nav.webp" alt="CarHipo" className="h-7 w-auto" />
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="md:hidden w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/5">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* User Card */}
        <div className="px-3 pt-4 pb-2">
          <div className="flex items-center gap-3 px-2 py-2.5 rounded-xl bg-white/[0.03] border border-white/5">
            <div className="w-9 h-9 rounded-full bg-cyan-500/20 flex items-center justify-center border border-cyan-500/30 overflow-hidden shrink-0">
              {user?.avatar_url ? (
                <img src={`${backendBase}${user.avatar_url}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-4 h-4 text-cyan-500" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate text-white">{user?.name}</p>
              <p className="text-[10px] text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-2 overflow-y-auto">
          <p className="px-2 mb-2 text-[9px] font-semibold uppercase tracking-[0.15em] text-gray-600">{t('dash.menu') || 'Menu'}</p>
          <div className="space-y-0.5">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all ${
                  activeSection === s.id
                    ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/15'
                    : 'text-gray-400 hover:text-white hover:bg-white/[0.04] border border-transparent'
                }`}
                data-testid={`dash-nav-${s.id}`}
              >
                <s.icon className="w-4 h-4 shrink-0" />
                {label(s)}
                {activeSection === s.id && <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 ml-auto" />}
              </button>
            ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="px-3 py-3 border-t border-white/5 shrink-0 space-y-1">
          <Link to="/" className="flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] text-gray-500 hover:text-white hover:bg-white/[0.04] transition-colors">
            <ChevronLeft className="w-4 h-4" /> {t('dash.backToMarketplace') || 'Back to Marketplace'}
          </Link>
          <button onClick={handleLogout} className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-[13px] text-red-400/70 hover:text-red-400 hover:bg-red-500/5 transition-colors" data-testid="dash-logout">
            <LogOut className="w-4 h-4" /> {t('nav.logout') || 'Log Out'}
          </button>
        </div>
      </aside>

      {/* ===== MAIN AREA ===== */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <header className="h-14 flex items-center gap-3 px-4 md:px-6 border-b border-white/5 bg-[#030810]/80 backdrop-blur-xl shrink-0 z-10">
          {/* Mobile menu toggle */}
          <button onClick={() => setSidebarOpen(true)} className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/5" data-testid="dash-menu-toggle">
            <Menu className="w-5 h-5" />
          </button>

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 hidden sm:inline">{t('nav.dashboard') || 'Dashboard'}</span>
            <ChevronRight className="w-3 h-3 text-gray-700 hidden sm:block" />
            <span className="font-medium text-white capitalize">{
              activeSection.startsWith('edit-car') ? (t('common.edit') + ' ' + t('dash.myCars').slice(0, -1)) :
              (label(SECTIONS.find(s => s.id === activeSection) || SECTIONS[0]))
            }</span>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Link to="/" className="text-[11px] text-gray-500 hover:text-cyan-400 transition-colors hidden sm:flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> View Site
            </Link>
          </div>
        </header>

        {/* Content Area - scrollable */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-5xl mx-auto px-4 lg:px-8 py-6">
            <ErrorBoundary>
              {activeSection === 'overview' && <OverviewSection token={token} user={user} setSection={setSection} />}
              {activeSection === 'cars' && <MyCarsSection token={token} user={user} setSection={setSection} />}
              {activeSection === 'add-car' && <AddCarVINSection token={token} />}
              {activeSection === 'bulk-import' && <BulkImportSection token={token} />}
              {activeSection === 'favorites' && <FavoritesSection token={token} />}
              {activeSection === 'saved-searches' && <SavedSearchesSection token={token} />}
              {activeSection === 'messages' && <MessagesSection token={token} user={user} />}
              {activeSection === 'profile' && <ProfileSection token={token} user={user} />}
              {activeSection === 'settings' && <SettingsSection token={token} user={user} />}
              {activeSection.startsWith('edit-car') && <EditCarSection token={token} carId={activeSection.replace('edit-car/', '')} />}
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
};

/* ===================== OVERVIEW ===================== */
const OverviewSection = ({ token, user, setSection }) => {
  const [stats, setStats] = useState({ cars: 0, views: 0, messages: 0 });
  useEffect(() => {
    axios.get(`${API}/my-cars`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const cars = r.data;
        setStats({ cars: cars.length, views: cars.reduce((s, c) => s + (c.views || 0), 0), messages: 0 });
      }).catch(() => {});
  }, [token]);

  return (
    <div>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1">Welcome back, {user?.name}</h1>
      <p className="text-sm text-gray-500 mb-6">Here&apos;s what&apos;s happening with your listings</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {[
          { label: 'Listings', value: stats.cars, icon: Car },
          { label: 'Total Views', value: stats.views, icon: Eye },
          { label: 'Messages', value: stats.messages, icon: MessageCircle },
          { label: 'Favorites', value: 0, icon: Heart },
        ].map((s, i) => (
          <div key={i} className="rounded-xl p-4 bg-white/[0.02] border border-white/5 hover:border-white/10 transition-colors">
            <s.icon className="w-5 h-5 text-cyan-500/70 mb-3" />
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-600 mb-3">Quick Actions</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button onClick={() => setSection('add-car')} className="rounded-xl p-5 bg-white/[0.02] border border-white/5 text-left hover:border-cyan-500/20 transition-all group" data-testid="quick-add-car">
          <Zap className="w-5 h-5 text-cyan-500 mb-2.5" />
          <h3 className="text-sm font-semibold mb-0.5 group-hover:text-cyan-400 transition-colors">Quick Add via VIN</h3>
          <p className="text-[11px] text-gray-600">AI fills the car details</p>
        </button>
        <button onClick={() => setSection('bulk-import')} className="rounded-xl p-5 bg-white/[0.02] border border-white/5 text-left hover:border-cyan-500/20 transition-all group" data-testid="quick-bulk-import">
          <Package className="w-5 h-5 text-cyan-500 mb-2.5" />
          <h3 className="text-sm font-semibold mb-0.5 group-hover:text-cyan-400 transition-colors">Bulk Import</h3>
          <p className="text-[11px] text-gray-600">VIN list, CSV, or API</p>
        </button>
        <button onClick={() => setSection('profile')} className="rounded-xl p-5 bg-white/[0.02] border border-white/5 text-left hover:border-cyan-500/20 transition-all group">
          <User className="w-5 h-5 text-cyan-500 mb-2.5" />
          <h3 className="text-sm font-semibold mb-0.5 group-hover:text-cyan-400 transition-colors">Edit Profile</h3>
          <p className="text-[11px] text-gray-600">Update your info</p>
        </button>
      </div>
    </div>
  );
};

/* ===================== MY CARS ===================== */
const MyCarsSection = ({ token, user, setSection }) => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/my-cars`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCars(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const handleDelete = async (carId) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await axios.delete(`${API}/cars/${carId}`, { headers: { Authorization: `Bearer ${token}` } });
      setCars(prev => prev.filter(c => c.id !== carId));
      toast.success('Listing deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = filter === 'all' ? cars : cars.filter(c => c.status === filter || (filter === 'active' && !c.status));

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-heading text-2xl font-bold">My Cars</h1>
        <Button size="sm" onClick={() => setSection('add-car')} className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Add Car</Button>
      </div>
      <div className="flex gap-2 mb-4">
        {['all', 'active', 'draft'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize ${filter === f ? 'bg-cyan-500/15 text-cyan-400' : 'text-gray-500 hover:text-white'}`}>{f} ({f === 'all' ? cars.length : cars.filter(c => f === 'active' ? (!c.status || c.status === 'active') : c.status === f).length})</button>
        ))}
      </div>
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-white/[0.02] border border-white/5">
          <Car className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 text-sm mb-4">No listings yet</p>
          <Button onClick={() => setSection('add-car')} className="bg-cyan-500 text-black hover:bg-cyan-400 text-xs"><Plus className="w-3 h-3 mr-1" /> List Your Car</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(car => (
            <div key={car.id} className="rounded-xl p-3 bg-white/[0.02] border border-white/5 flex gap-3 hover:border-white/10 transition-colors" data-testid={`my-car-${car.id}`}>
              <img src={car.images?.[0] || 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400'} alt="" className="w-28 sm:w-36 h-20 sm:h-24 object-cover rounded-lg shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-heading text-sm font-semibold truncate">{car.title}</h3>
                  {car.status === 'draft' && <span className="text-[9px] px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400 shrink-0">Draft</span>}
                </div>
                <p className="text-cyan-500 font-bold text-base">{car.price > 0 ? `\u20AC${car.price.toLocaleString()}` : 'Set price'}</p>
                <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-1">
                  <span>{car.year}</span>
                  <span>{car.mileage?.toLocaleString()} km</span>
                  <span className="flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" /> {car.views}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <Link to={`/cars/${car.id}`}><Button variant="outline" size="sm" className="h-7 text-[10px] border-white/10 w-full">View</Button></Link>
                <Button variant="outline" size="sm" onClick={() => setSection(`edit-car/${car.id}`)} className="h-7 text-[10px] border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 w-full" data-testid={`edit-car-${car.id}`}><Edit3 className="w-3 h-3" /></Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(car.id)} className="h-7 text-[10px] border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ===================== ADD CAR VIN ===================== */
const WIZARD_STEPS = [
  { id: 1, label: 'Identify', desc: 'VIN & basics' },
  { id: 2, label: 'Specs', desc: 'Body & engine' },
  { id: 3, label: 'History', desc: 'Owners & condition' },
  { id: 4, label: 'Photos', desc: 'Upload images' },
  { id: 5, label: 'Description', desc: 'Tell the story' },
  { id: 6, label: 'Price & Publish', desc: 'Set price & contact' },
];

const FUEL_OPTIONS = ['Petrol', 'Diesel', 'Hybrid', 'Electric', 'LPG', 'CNG'];
const TRANS_OPTIONS = ['Automatic', 'Manual', 'Semi-automatic', 'CVT'];
const BODY_OPTIONS = ['Sedan', 'SUV', 'Hatchback', 'Coupe', 'Wagon', 'Convertible', 'Pickup', 'Van', 'Minivan'];
const DRIVE_OPTIONS = ['FWD', 'RWD', 'AWD', '4WD'];
const SERVICE_OPTIONS = [
  { v: 'full', label: 'Full service history' },
  { v: 'partial', label: 'Partial' },
  { v: 'none', label: 'No history' },
];
const COMMON_FEATURES = [
  'Leather seats', 'Sunroof', 'Panoramic roof', 'Navigation', 'Heated seats',
  'Cooled seats', 'Apple CarPlay', 'Android Auto', 'Bluetooth', 'Cruise control',
  'Adaptive cruise', 'Lane assist', 'Blind spot monitor', '360° camera', 'Parking sensors',
  'Keyless entry', 'Push-button start', 'LED headlights', 'Tow hitch', 'Alloy wheels',
];

const AddCarVINSection = ({ token }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [vin, setVin] = useState(() => (searchParams.get('vin') || '').toUpperCase());
  const [vinHint, setVinHint] = useState('');
  const [decoding, setDecoding] = useState(false);
  const [decoded, setDecoded] = useState(null);
  const [autoFields, setAutoFields] = useState([]);
  const [needsInput, setNeedsInput] = useState([]);
  const [aiGen, setAiGen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef(null);
  const backendBase = API.replace('/api', '');

  const [form, setForm] = useState({
    title: '', brand: '', model: '', year: '', price: '', mileage: '',
    fuel_type: 'Petrol', transmission: 'Automatic', body_type: 'Sedan',
    color: '', interior_color: '', description: '', location: '', condition: 'used',
    engine: '', engine_power_hp: '', engine_displacement: '', trim: '', drive_type: 'FWD',
    doors: 4, seats: 5, previous_owners: 1, accident_free: true, service_history: 'full',
    first_registration: '', mot_until: '', features: [], fuel_consumption: '',
    co2_emissions: '', contact_phone: '', contact_preference: 'chat',
  });

  const updateForm = (patch) => setForm(p => ({ ...p, ...patch }));
  const toggleFeature = (f) => setForm(p => ({
    ...p,
    features: p.features.includes(f) ? p.features.filter(x => x !== f) : [...p.features, f],
  }));

  const handleDecode = async () => {
    if (vin.length !== 17) { toast.error('VIN must be 17 characters'); return; }
    setDecoding(true);
    try {
      const r = await axios.post(`${API}/vin-decode`, { vin });
      const data = r?.data || {};
      const d = data.decoded || {};
      const autoFilled = Array.isArray(data.auto_filled) ? data.auto_filled : [];
      const needsInputArr = Array.isArray(data.needs_input) ? data.needs_input : [];
      // Pre-compute patch outside setState updater to keep crashes inside try/catch
      const titlePart = `${d.year || ''} ${d.brand || ''} ${d.model || ''}`.trim();
      const patch = {
        brand: d.brand || undefined,
        model: d.model || undefined,
        year: d.year || undefined,
        fuel_type: d.fuel_type || undefined,
        transmission: d.transmission || undefined,
        body_type: d.body_type || undefined,
        color: d.color || undefined,
        engine: d.engine || undefined,
        trim: d.trim || undefined,
        engine_power_hp: d.engine_power_hp || undefined,
        engine_displacement: d.engine_displacement || undefined,
        drive_type: d.drive_type || undefined,
        doors: d.doors || undefined,
        title: titlePart || undefined,
      };
      setDecoded(d);
      setAutoFields(autoFilled);
      setNeedsInput(needsInputArr);
      setForm(prev => {
        const next = { ...prev };
        Object.keys(patch).forEach(k => { if (patch[k] !== undefined) next[k] = patch[k]; });
        return next;
      });
      if (autoFilled.length > 0) {
        toast.success('VIN decoded — fields auto-filled');
      } else {
        toast.warning('VIN not recognized — fill manually');
      }
    } catch (err) {
      console.error('VIN decode error:', err);
      toast.error('VIN decode failed — fill manually');
    } finally { setDecoding(false); }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const newImages = [...images];
    for (const file of files) {
      if (newImages.length >= 10) break;
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
        newImages.push({ url: r.data.url, name: file.name });
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setImages(newImages);
    setUploading(false);
    if (imgRef.current) imgRef.current.value = '';
  };

  const removeImage = (idx) => setImages(prev => prev.filter((_, i) => i !== idx));
  const moveImage = (idx, dir) => setImages(prev => {
    const next = [...prev];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return next;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  const generateAIDescription = async () => {
    setAiGen(true);
    try {
      const r = await axios.post(`${API}/generate-description`, {
        specs: {
          year: form.year, brand: form.brand, model: form.model, trim: form.trim,
          mileage: form.mileage, fuel_type: form.fuel_type, transmission: form.transmission,
          body_type: form.body_type, engine: form.engine, color: form.color,
          condition: form.condition, features: form.features,
          accident_free: form.accident_free, service_history: form.service_history,
        },
      });
      updateForm({ description: r.data.description || '' });
      toast.success('AI description generated');
    } catch { toast.error('AI description failed'); }
    finally { setAiGen(false); }
  };

  const validateStep = (s) => {
    if (s === 1) {
      if (!form.brand.trim()) return { field: 'brand', msg: 'Brand is required' };
      if (!form.model.trim()) return { field: 'model', msg: 'Model is required' };
      if (!form.year) return { field: 'year', msg: 'Year is required' };
    }
    if (s === 6) {
      if (!form.price || parseFloat(form.price) <= 0) return { field: 'price', msg: 'Set a valid price' };
      if (!form.location.trim()) return { field: 'location', msg: 'Location is required' };
    }
    return null;
  };

  const [invalidField, setInvalidField] = useState(null);

  const next = () => {
    const err = validateStep(step);
    if (err) {
      toast.error(err.msg);
      setInvalidField(err.field);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="car-field-${err.field}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (typeof el.focus === 'function') setTimeout(() => el.focus(), 300);
        }
      }, 50);
      return;
    }
    setInvalidField(null);
    setStep(s => Math.min(6, s + 1));
  };
  const back = () => { setInvalidField(null); setStep(s => Math.max(1, s - 1)); };

  const handleSubmit = async () => {
    const err = validateStep(1) || validateStep(6);
    if (err) {
      toast.error(err.msg);
      setInvalidField(err.field);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="car-field-${err.field}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (typeof el.focus === 'function') setTimeout(() => el.focus(), 300);
        }
      }, 50);
      return;
    }
    setInvalidField(null);
    if (!form.title.trim()) updateForm({ title: `${form.year} ${form.brand} ${form.model}`.trim() });
    setSubmitting(true);
    try {
      await axios.post(`${API}/cars`, {
        title: (form.title || `${form.year} ${form.brand} ${form.model}`).trim(),
        brand: form.brand.trim(), model: form.model.trim(),
        year: parseInt(form.year) || 2024, price: parseFloat(form.price),
        mileage: parseInt(form.mileage) || 0,
        fuel_type: form.fuel_type, transmission: form.transmission, body_type: form.body_type,
        color: form.color, interior_color: form.interior_color, description: form.description,
        location: form.location, condition: form.condition,
        images: images.map(img => img.url), vin: vin || '',
        engine: form.engine, trim: form.trim, drive_type: form.drive_type,
        engine_power_hp: parseInt(form.engine_power_hp) || 0,
        engine_displacement: parseInt(form.engine_displacement) || 0,
        doors: parseInt(form.doors) || 4, seats: parseInt(form.seats) || 5,
        previous_owners: parseInt(form.previous_owners) || 1,
        accident_free: !!form.accident_free, service_history: form.service_history,
        first_registration: form.first_registration, mot_until: form.mot_until,
        features: form.features,
        fuel_consumption: parseFloat(form.fuel_consumption) || 0,
        co2_emissions: parseInt(form.co2_emissions) || 0,
        contact_phone: form.contact_phone, contact_preference: form.contact_preference,
        status: 'active',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Car listed!');
      navigate('/dashboard/cars');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create listing'); }
    finally { setSubmitting(false); }
  };

  const fieldClass = (field) => {
    const isInvalid = invalidField === field;
    if (isInvalid) return 'border-red-500/60 bg-red-500/5 ring-1 ring-red-500/30';
    if (!decoded) return 'border-white/10';
    if (autoFields.includes(field)) return 'border-green-500/40 bg-green-500/5';
    if (needsInput.includes(field)) return 'border-yellow-500/40 bg-yellow-500/5 ring-1 ring-yellow-500/20';
    return 'border-white/10';
  };

  const renderField = (label, k, opts = {}) => {
    const { type = 'text', span = false, options = null } = opts;
    return (
      <div key={k} className={span ? 'sm:col-span-2' : ''}>
        <label className="text-[10px] text-gray-500 mb-1 block uppercase tracking-wider">{label}</label>
        {options ? (
          <select
            value={form[k] || ''}
            onChange={e => updateForm({ [k]: e.target.value })}
            className={`w-full h-9 text-sm rounded-md bg-black/30 px-3 border ${fieldClass(k)} focus:outline-none focus:ring-1 focus:ring-cyan-500/40`}
            data-testid={`car-field-${k}`}
          >
            {options.map(o => typeof o === 'string'
              ? <option key={o} value={o}>{o}</option>
              : <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        ) : (
          <Input value={form[k] ?? ''} onChange={e => updateForm({ [k]: e.target.value })} type={type} className={`h-9 text-sm bg-black/30 ${fieldClass(k)}`} data-testid={`car-field-${k}`} />
        )}
      </div>
    );
  };

  return (
    <div data-testid="add-car-wizard">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">List Your Car</h1>
          <p className="text-xs text-gray-500">Step {step} of 6 — {WIZARD_STEPS[step - 1].desc}</p>
        </div>
        <div className="text-[10px] text-gray-500">Auto-saved locally</div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1">
        {WIZARD_STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <button
              onClick={() => { if (s.id < step) setStep(s.id); }}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-md shrink-0 transition-colors ${
                s.id === step ? 'bg-cyan-500/10 text-cyan-300' : s.id < step ? 'text-gray-300 hover:bg-white/5' : 'text-gray-600'
              }`}
              data-testid={`wizard-step-${s.id}`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                s.id === step ? 'bg-cyan-500 text-black' : s.id < step ? 'bg-green-500/30 text-green-300' : 'bg-white/5 text-gray-500'
              }`}>{s.id < step ? <Check className="w-3 h-3" /> : s.id}</span>
              <span className="text-[11px] font-semibold hidden sm:inline">{s.label}</span>
            </button>
            {i < WIZARD_STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-gray-700 shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      <div className="rounded-xl p-4 sm:p-5 bg-white/[0.02] border border-white/5 min-h-[320px]">
        {/* STEP 1 — IDENTIFY */}
        {step === 1 && (
          <div className="space-y-5" data-testid="wizard-pane-1">
            <div>
              <label className="text-[10px] text-gray-500 mb-2 block uppercase tracking-wider font-semibold">Decode by VIN (optional, saves time)</label>
              <div className="flex gap-2">
                <Input
                  value={vin}
                  onChange={e => {
                    const before = e.target.value.toUpperCase().replace(/\s+/g, '');
                    const after = before.replace(/[IOQ]/g, (c) => ({ I: '1', O: '0', Q: '0' }[c])).slice(0, 17);
                    setVin(after);
                    setVinHint(before !== after ? 'Auto-converted I→1, O→0, Q→0' : '');
                  }}
                  maxLength={17}
                  placeholder="17-character VIN (letters I/O/Q auto-convert)"
                  className="font-mono tracking-wider bg-black/50 border-white/10 h-11"
                  data-testid="vin-decode-input"
                />
                <Button onClick={handleDecode} disabled={decoding || vin.length !== 17} className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold h-11 px-5 shrink-0" data-testid="vin-decode-btn">
                  {decoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-1" /> Decode</>}
                </Button>
              </div>
              <div className="flex items-center justify-between mt-1 gap-2 flex-wrap">
                <p className="text-[10px] text-gray-600">
                  <span className={vin.length === 17 ? 'text-green-400' : ''}>{vin.length}/17 characters</span>
                  {decoded && <span className="text-green-400"> — decoded ✓</span>}
                  {vinHint && <span className="text-cyan-400 ml-2" data-testid="vin-hint">· {vinHint}</span>}
                </p>
                {decoded && vin.length === 17 && (
                  <Link to={`/vin-report/${vin}`} target="_blank" rel="noreferrer" className="text-[10px] text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1" data-testid="wizard-vin-report-link">
                    View full vehicle report <ChevronRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </div>

            <div className="border-t border-white/5 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {renderField('Brand *', 'brand')}
                {renderField('Model *', 'model')}
                {renderField('Year *', 'year', { type: 'number' })}
                {renderField('Mileage (km)', 'mileage', { type: 'number' })}
                {renderField('Condition', 'condition', { options: ['new', 'used', 'demo'] })}
                {renderField('Trim / Edition', 'trim')}
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 — SPECS */}
        {step === 2 && (
          <div className="space-y-4" data-testid="wizard-pane-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {renderField('Body Type', 'body_type', { options: BODY_OPTIONS })}
              {renderField('Fuel Type', 'fuel_type', { options: FUEL_OPTIONS })}
              {renderField('Transmission', 'transmission', { options: TRANS_OPTIONS })}
              {renderField('Drivetrain', 'drive_type', { options: DRIVE_OPTIONS })}
              {renderField('Engine', 'engine')}
              {renderField('Power (hp)', 'engine_power_hp', { type: 'number' })}
              {renderField('Displacement (cc)', 'engine_displacement', { type: 'number' })}
              {renderField('Doors', 'doors', { type: 'number' })}
              {renderField('Seats', 'seats', { type: 'number' })}
              {renderField('Exterior Color', 'color')}
              {renderField('Interior Color', 'interior_color')}
              {renderField('Fuel Cons. (l/100km)', 'fuel_consumption', { type: 'number' })}
              {renderField('CO₂ (g/km)', 'co2_emissions', { type: 'number' })}
            </div>
          </div>
        )}

        {/* STEP 3 — HISTORY */}
        {step === 3 && (
          <div className="space-y-4" data-testid="wizard-pane-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {renderField('Previous Owners', 'previous_owners', { type: 'number' })}
              {renderField('Service History', 'service_history', { options: SERVICE_OPTIONS })}
              {renderField('First Registration', 'first_registration', { type: 'date' })}
              {renderField('MOT / TÜV Until', 'mot_until', { type: 'date' })}
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-black/30 border border-white/5">
              <input
                type="checkbox"
                id="accident_free"
                checked={!!form.accident_free}
                onChange={e => updateForm({ accident_free: e.target.checked })}
                className="w-4 h-4 accent-cyan-500"
                data-testid="car-field-accident_free"
              />
              <label htmlFor="accident_free" className="text-sm cursor-pointer">
                <span className="font-semibold">Accident-free</span>
                <span className="text-gray-500 text-xs block">No reported accidents or major damage</span>
              </label>
            </div>
          </div>
        )}

        {/* STEP 4 — PHOTOS */}
        {step === 4 && (
          <div data-testid="wizard-pane-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Photos ({images.length}/10)</label>
              <Button variant="outline" size="sm" onClick={() => imgRef.current?.click()} disabled={uploading || images.length >= 10} className="text-xs h-8 border-white/10" data-testid="upload-photos-btn">
                {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                {uploading ? 'Uploading...' : 'Add Photos'}
              </Button>
              <input ref={imgRef} type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" data-testid="photo-file-input" />
            </div>
            {images.length === 0 ? (
              <div className="border-2 border-dashed border-white/10 rounded-lg p-10 text-center cursor-pointer hover:border-cyan-500/30 transition-colors" onClick={() => imgRef.current?.click()}>
                <Camera className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Click to add car photos</p>
                <p className="text-[10px] text-gray-600 mt-1">JPG, PNG, WebP up to 10MB each. Max 10 photos. First photo will be the cover.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {images.map((img, i) => (
                  <div key={i} className="relative group aspect-[4/3] rounded-lg overflow-hidden bg-white/5">
                    <img src={`${backendBase}${img.url}`} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                      <button onClick={() => moveImage(i, -1)} disabled={i === 0} className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center"><ChevronLeft className="w-3.5 h-3.5" /></button>
                      <button onClick={() => moveImage(i, 1)} disabled={i === images.length - 1} className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center"><ChevronRight className="w-3.5 h-3.5" /></button>
                      <button onClick={() => removeImage(i)} className="w-7 h-7 rounded-full bg-red-500/80 hover:bg-red-500 flex items-center justify-center" data-testid={`remove-photo-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] px-1.5 py-0.5 bg-cyan-500 text-black rounded font-bold">Cover</span>}
                  </div>
                ))}
                {images.length < 10 && (
                  <div className="aspect-[4/3] rounded-lg border-2 border-dashed border-white/10 flex items-center justify-center cursor-pointer hover:border-cyan-500/30 transition-colors" onClick={() => imgRef.current?.click()}>
                    <Plus className="w-6 h-6 text-gray-600" />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* STEP 5 — DESCRIPTION & FEATURES */}
        {step === 5 && (
          <div className="space-y-5" data-testid="wizard-pane-5">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Description</label>
                <Button variant="outline" size="sm" onClick={generateAIDescription} disabled={aiGen || !form.brand || !form.model} className="text-xs h-7 border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10" data-testid="ai-description-btn">
                  {aiGen ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />} AI Generate
                </Button>
              </div>
              <Textarea
                value={form.description}
                onChange={e => updateForm({ description: e.target.value })}
                rows={6}
                placeholder="Describe what makes your car special — recent service, upgrades, why you're selling…"
                className="text-sm bg-black/30 border-white/10"
                data-testid="car-field-description"
              />
              <p className="text-[10px] text-gray-600 mt-1">{form.description.length} characters</p>
            </div>

            <div>
              <label className="text-[10px] text-gray-500 mb-2 block uppercase tracking-wider font-semibold">Features & Equipment ({form.features.length})</label>
              <div className="flex flex-wrap gap-2">
                {COMMON_FEATURES.map(f => {
                  const active = form.features.includes(f);
                  return (
                    <button
                      key={f}
                      onClick={() => toggleFeature(f)}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                        active ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-200' : 'bg-black/30 border-white/10 text-gray-400 hover:border-white/20'
                      }`}
                      data-testid={`feature-${f.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {active && <Check className="w-2.5 h-2.5 inline mr-1" />}
                      {f}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* STEP 6 — PRICE & PUBLISH */}
        {step === 6 && (
          <div className="space-y-4" data-testid="wizard-pane-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {renderField('Title', 'title', { span: true })}
              {renderField('Price (€) *', 'price', { type: 'number' })}
              {renderField('Location *', 'location')}
              {renderField('Contact Phone', 'contact_phone')}
              {renderField('Preferred Contact', 'contact_preference', { options: [
                { v: 'chat', label: 'In-app chat' },
                { v: 'phone', label: 'Phone call' },
                { v: 'email', label: 'Email' },
              ] })}
            </div>

            <div className="rounded-lg bg-black/30 border border-white/5 p-3">
              <p className="text-[10px] text-gray-500 mb-2 uppercase tracking-wider font-semibold">Listing Summary</p>
              <div className="grid grid-cols-2 gap-y-1 text-xs">
                <span className="text-gray-500">Vehicle</span><span className="font-semibold">{form.year} {form.brand} {form.model}</span>
                <span className="text-gray-500">Specs</span><span>{form.body_type} · {form.fuel_type} · {form.transmission}</span>
                <span className="text-gray-500">Mileage</span><span>{form.mileage || '—'} km</span>
                <span className="text-gray-500">Photos</span><span>{images.length} uploaded</span>
                <span className="text-gray-500">Features</span><span>{form.features.length} selected</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Wizard Nav */}
      <div className="flex items-center justify-between mt-5">
        <Button variant="outline" onClick={back} disabled={step === 1} className="border-white/10 h-10 px-5" data-testid="wizard-back-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        {step < 6 ? (
          <Button onClick={next} className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold h-10 px-6" data-testid="wizard-next-btn">
            Continue <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={submitting} className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold h-10 px-6" data-testid="submit-car-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />} Publish Listing
          </Button>
        )}
      </div>
    </div>
  );
};

/* ===================== BULK IMPORT ===================== */
const BulkImportSection = ({ token }) => {
  const [tab, setTab] = useState('vin');
  const [vinText, setVinText] = useState('');
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const fileRef = useRef(null);

  const handleVinImport = async () => {
    const vins = vinText.split('\n').map(v => v.trim()).filter(v => v.length > 0);
    if (vins.length === 0) { toast.error('Enter at least one VIN'); return; }
    setImporting(true);
    try {
      const r = await axios.post(`${API}/bulk-import/vin-list`, { vins }, { headers: { Authorization: `Bearer ${token}` } });
      setResults(r.data);
      toast.success(`Imported ${r.data.succeeded}/${r.data.total} cars`);
    } catch { toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/bulk-import/csv`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      setResults(r.data);
      toast.success(`Imported ${r.data.succeeded}/${r.data.total} cars`);
    } catch { toast.error('CSV import failed'); }
    finally { setImporting(false); }
  };

  const generateApiKey = async () => {
    try {
      const r = await axios.post(`${API}/bulk-import/api-key`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setApiKey(r.data.api_key);
      toast.success('API key generated');
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-1">Bulk Import</h1>
      <p className="text-xs text-gray-500 mb-5">Import multiple cars at once for your dealership</p>

      <div className="flex gap-2 mb-5 overflow-x-auto scrollbar-hide">
        {[{ id: 'vin', label: 'VIN List', icon: FileText }, { id: 'csv', label: 'CSV Upload', icon: Upload }, { id: 'api', label: 'API Key', icon: Key }].map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setResults(null); }} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${tab === t.id ? 'bg-cyan-500/15 text-cyan-400' : 'text-gray-500 hover:text-white bg-white/[0.03]'}`} data-testid={`bulk-tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'vin' && (
        <div className="rounded-xl p-4 bg-white/[0.02] border border-white/5">
          <p className="text-xs text-gray-400 mb-3">Paste VIN codes, one per line (max 50). AI will decode each and create draft listings.</p>
          <Textarea value={vinText} onChange={e => setVinText(e.target.value.toUpperCase())} rows={6} placeholder={"WBA123456789ABCDE\n5YJ3E1EA1NF123456\nWDDWJ8GB5KA789012"} className="font-mono text-xs bg-black/30 border-white/10 mb-3" data-testid="bulk-vin-textarea" />
          <Button onClick={handleVinImport} disabled={importing} className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-xs h-9" data-testid="bulk-vin-import-btn">
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Zap className="w-3.5 h-3.5 mr-1" />} Import {vinText.split('\n').filter(v => v.trim()).length} VINs
          </Button>
        </div>
      )}

      {tab === 'csv' && (
        <div className="rounded-xl p-4 bg-white/[0.02] border border-white/5">
          <p className="text-xs text-gray-400 mb-3">Upload a CSV file with car data.</p>
          <Button variant="outline" onClick={() => window.open(`${API}/bulk-import/csv-template`, '_blank')} className="text-xs h-9 border-white/10 mb-4"><Download className="w-3.5 h-3.5 mr-1" /> Download Template</Button>
          <div className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center hover:border-cyan-500/30 transition-colors cursor-pointer" onClick={() => fileRef.current?.click()}>
            <Upload className="w-8 h-8 text-gray-600 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Click to upload CSV</p>
            <p className="text-[10px] text-gray-600 mt-1">Max 100 rows</p>
            <input ref={fileRef} type="file" accept=".csv" onChange={handleCSVImport} className="hidden" data-testid="csv-file-input" />
          </div>
          {importing && <div className="flex items-center gap-2 mt-3 text-xs text-cyan-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing...</div>}
        </div>
      )}

      {tab === 'api' && (
        <div className="rounded-xl p-4 bg-white/[0.02] border border-white/5">
          <p className="text-xs text-gray-400 mb-3">Generate an API key for programmatic imports.</p>
          {apiKey ? (
            <div className="mb-4">
              <label className="text-[10px] text-gray-500 mb-1 block">Your API Key</label>
              <div className="flex gap-2">
                <Input value={apiKey} readOnly className="font-mono text-xs bg-black/30 border-white/10" />
                <Button variant="outline" onClick={() => { navigator.clipboard.writeText(apiKey); toast.success('Copied!'); }} className="text-xs h-9 border-white/10 shrink-0">Copy</Button>
              </div>
            </div>
          ) : (
            <Button onClick={generateApiKey} className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-xs h-9 mb-4" data-testid="generate-api-key-btn"><Key className="w-3.5 h-3.5 mr-1" /> Generate API Key</Button>
          )}
          <div className="rounded-lg p-3 bg-black/20 border border-white/5 mt-3">
            <code className="text-[10px] text-cyan-400 block mb-2">POST /api/bulk-import/api</code>
            <pre className="text-[9px] text-gray-500 overflow-x-auto">{`{ "api_key": "ch_...", "cars": [{ "brand": "BMW", ... }] }`}</pre>
          </div>
        </div>
      )}

      {results && (
        <div className="rounded-xl p-4 bg-white/[0.02] border border-white/5 mt-4" data-testid="import-results">
          <h3 className="font-heading text-sm font-semibold mb-3">Results</h3>
          <div className="flex gap-4 mb-3 text-xs">
            <span className="text-green-400"><Check className="w-3 h-3 inline mr-0.5" /> {results.succeeded} ok</span>
            {results.failed > 0 && <span className="text-red-400"><X className="w-3 h-3 inline mr-0.5" /> {results.failed} failed</span>}
          </div>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {results.results?.map((r, i) => (
              <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${r.status === 'success' ? 'bg-green-500/5 border border-green-500/10' : 'bg-red-500/5 border border-red-500/10'}`}>
                {r.status === 'success' ? <Check className="w-3 h-3 text-green-500 shrink-0" /> : <AlertCircle className="w-3 h-3 text-red-500 shrink-0" />}
                <span className="font-mono truncate">{r.vin || r.row || r.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ===================== PROFILE ===================== */
const ProfileSection = ({ token, user }) => {
  const { refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const avatarRef = useRef(null);
  const coverRef = useRef(null);
  const backendBase = API.replace('/api', '');

  useEffect(() => {
    if (user?.id) {
      axios.get(`${API}/profile/${user.id}`).then(r => {
        setProfile(r.data);
        setForm({ name: r.data.name || '', bio: r.data.bio || '', phone: r.data.phone || '', location: r.data.location || '', website: r.data.website || '', social_instagram: r.data.social_links?.instagram || '', social_facebook: r.data.social_links?.facebook || '', social_twitter: r.data.social_links?.twitter || '' });
      }).catch(() => {});
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/profile`, { name: form.name, bio: form.bio, phone: form.phone, location: form.location, website: form.website, social_links: { instagram: form.social_instagram, facebook: form.social_facebook, twitter: form.social_twitter } }, { headers: { Authorization: `Bearer ${token}` } });
      setProfile(r.data);
      if (refreshUser) await refreshUser();
      toast.success('Profile updated');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const uploadFile = async (file, endpoint) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/${endpoint}`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5 MB)'); return; }
    try {
      const r = await uploadFile(file, 'profile/avatar');
      setProfile(p => ({ ...p, avatar_url: r.data.url }));
      if (refreshUser) await refreshUser();
      toast.success('Avatar updated');
    } catch { toast.error('Upload failed'); }
  };
  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Image too large (max 8 MB)'); return; }
    try {
      const r = await uploadFile(file, 'profile/cover');
      setProfile(p => ({ ...p, cover_url: r.data.url }));
      if (refreshUser) await refreshUser();
      toast.success('Cover updated');
    } catch { toast.error('Upload failed'); }
  };

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-5">Edit Profile</h1>

      <div className="rounded-xl overflow-hidden mb-5 bg-white/[0.02] border border-white/5">
        <div className="relative h-32 sm:h-44 bg-gradient-to-r from-cyan-900/20 to-transparent cursor-pointer group" onClick={() => coverRef.current?.click()}>
          {profile?.cover_url && <img src={`${backendBase}${profile.cover_url}`} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><Camera className="w-6 h-6 text-white" /></div>
          <input ref={coverRef} type="file" accept="image/*" onChange={uploadCover} className="hidden" />
        </div>
        <div className="px-4 pb-4 -mt-10 relative z-10">
          <div className="flex items-end gap-3">
            <div className="relative cursor-pointer group" onClick={() => avatarRef.current?.click()}>
              <div className="w-20 h-20 rounded-full bg-[#0a0a0f] border-4 border-[#060a14] overflow-hidden flex items-center justify-center">
                {(() => {
                  const raw = profile?.avatar_url || profile?.picture || '';
                  if (!raw) return <div className="w-full h-full bg-cyan-500/20 flex items-center justify-center"><User className="w-8 h-8 text-cyan-500" /></div>;
                  const src = raw.startsWith('http') ? raw : `${backendBase}${raw}`;
                  return <img src={src} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />;
                })()}
              </div>
              <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><Camera className="w-4 h-4 text-white" /></div>
              <input ref={avatarRef} type="file" accept="image/*" onChange={uploadAvatar} className="hidden" data-testid="profile-avatar-input" />
            </div>
            <div className="pb-1">
              <h2 className="font-heading text-lg font-bold">{profile?.name}</h2>
              <p className="text-xs text-gray-500">{profile?.email}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl p-4 bg-white/[0.02] border border-white/5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { key: 'name', label: 'Display Name', span: true },
          ].map(f => (
            <div key={f.key} className={f.span ? 'sm:col-span-2' : ''}>
              <label className="text-[10px] text-gray-500 mb-1 block">{f.label}</label>
              <Input value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} className="h-9 text-sm bg-black/30 border-white/10" />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="text-[10px] text-gray-500 mb-1 block">Bio</label>
            <Textarea value={form.bio || ''} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} rows={3} className="text-sm bg-black/30 border-white/10" placeholder="Tell others about yourself..." />
          </div>
          {[
            { key: 'phone', label: 'Phone', icon: Phone },
            { key: 'location', label: 'Location', icon: MapPin },
            { key: 'website', label: 'Website', icon: Globe },
            { key: 'social_instagram', label: 'Instagram', icon: Instagram, placeholder: '@username' },
            { key: 'social_facebook', label: 'Facebook', icon: Facebook },
            { key: 'social_twitter', label: 'Twitter / X', icon: Twitter, placeholder: '@handle' },
          ].map(f => (
            <div key={f.key}>
              <label className="text-[10px] text-gray-500 mb-1 flex items-center gap-1"><f.icon className="w-3 h-3" /> {f.label}</label>
              <Input value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} className="h-9 text-sm bg-black/30 border-white/10" placeholder={f.placeholder || ''} />
            </div>
          ))}
        </div>
        <div className="mt-5 flex items-center gap-3">
          <Button onClick={handleSave} disabled={saving} className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold h-10 px-6" data-testid="save-profile-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />} Save Profile
          </Button>
          <Link to={`/profile/${user?.id}`} className="text-xs text-cyan-500 hover:underline">View public profile</Link>
        </div>
      </div>
    </div>
  );
};

/* ===================== SETTINGS ===================== */
const SettingsSection = ({ token, user }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [changing, setChanging] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const isGoogleUser = !token && !!user;

  const changePassword = async () => {
    if (!oldPw || !newPw) { toast.error('Fill all fields'); return; }
    if (newPw.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPw !== confirmPw) { toast.error('New passwords do not match'); return; }
    setChanging(true);
    try {
      await axios.post(`${API}/auth/change-password`, { old_password: oldPw, new_password: newPw },
        { headers: { Authorization: `Bearer ${token}` } });
      setOldPw(''); setNewPw(''); setConfirmPw('');
      toast.success('Password updated successfully');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update password'); }
    finally { setChanging(false); }
  };

  const deleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') { toast.error('Type DELETE to confirm'); return; }
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/account`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true });
      toast.success('Account deleted');
      await logout();
      navigate('/');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete account'); }
    finally { setDeleting(false); }
  };

  return (
    <div data-testid="settings-section">
      <h1 className="font-heading text-2xl font-bold mb-1">Settings</h1>
      <p className="text-xs text-gray-500 mb-5">Manage your account, security and preferences</p>

      <div className="rounded-xl p-4 bg-white/[0.02] border border-white/5 mb-4">
        <h3 className="font-heading text-sm font-semibold mb-3">Account</h3>
        <div className="space-y-2 text-xs">
          <div className="flex justify-between items-center py-2 border-b border-white/5"><span className="text-gray-400">Email</span><span data-testid="settings-email">{user?.email}</span></div>
          <div className="flex justify-between items-center py-2 border-b border-white/5"><span className="text-gray-400">Name</span><span>{user?.name || '—'}</span></div>
          <div className="flex justify-between items-center py-2 border-b border-white/5"><span className="text-gray-400">Member Since</span><span>{user?.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</span></div>
          <div className="flex justify-between items-center py-2 border-b border-white/5"><span className="text-gray-400">Sign-in method</span><span className="text-cyan-400">{isGoogleUser ? 'Google' : 'Email + password'}</span></div>
          <div className="flex justify-between items-center py-2"><span className="text-gray-400">Account type</span><span className="text-cyan-400">{user?.is_admin ? 'Admin' : 'Standard'}</span></div>
        </div>
      </div>

      {!isGoogleUser && (
        <div className="rounded-xl p-4 bg-white/[0.02] border border-white/5 mb-4">
          <h3 className="font-heading text-sm font-semibold mb-3">Change password</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="text-[10px] text-gray-500 mb-1 block uppercase tracking-wider">Current</label>
              <Input type="password" value={oldPw} onChange={e => setOldPw(e.target.value)} className="h-9 text-sm bg-black/30 border-white/10" data-testid="settings-old-password" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 mb-1 block uppercase tracking-wider">New (min 6)</label>
              <Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} className="h-9 text-sm bg-black/30 border-white/10" data-testid="settings-new-password" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 mb-1 block uppercase tracking-wider">Confirm new</label>
              <Input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className="h-9 text-sm bg-black/30 border-white/10" data-testid="settings-confirm-password" />
            </div>
          </div>
          <Button onClick={changePassword} disabled={changing} className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold h-9 text-xs px-4" data-testid="settings-change-password-btn">
            {changing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            {changing ? 'Updating…' : 'Update password'}
          </Button>
        </div>
      )}

      {isGoogleUser && (
        <div className="rounded-xl p-4 bg-white/[0.02] border border-white/5 mb-4">
          <h3 className="font-heading text-sm font-semibold mb-2">Security</h3>
          <p className="text-xs text-gray-500">You signed in with Google. Manage your password from your <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline">Google account security settings</a>.</p>
        </div>
      )}

      <div className="rounded-xl p-4 bg-red-500/[0.04] border border-red-500/20">
        <h3 className="font-heading text-sm font-semibold mb-1 text-red-300">Danger zone</h3>
        <p className="text-xs text-gray-400 mb-3">Permanently deletes your account. Your listings will be archived. This cannot be undone.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder="Type DELETE to confirm" className="h-9 text-sm bg-black/30 border-red-500/30 max-w-xs" data-testid="settings-delete-confirm" />
          <Button onClick={deleteAccount} disabled={deleting || deleteConfirm !== 'DELETE'} variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10 h-9 text-xs" data-testid="settings-delete-btn">
            {deleting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
            {deleting ? 'Deleting…' : 'Delete my account'}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ===================== FAVORITES ===================== */
const FavoritesSection = ({ token }) => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/favorites`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCars(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const removeFav = async (carId) => {
    await axios.post(`${API}/favorites/${carId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setCars(prev => prev.filter(c => c.id !== carId));
    toast.success('Removed from favorites');
  };

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-5">Favorites</h1>
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />)}</div>
      ) : cars.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-white/[0.02] border border-white/5">
          <Heart className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 text-sm mb-2">No favorites yet</p>
          <p className="text-gray-600 text-xs">Click the heart icon on any car listing to save it here</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {cars.map(car => (
            <div key={car.id} className="relative group">
              <CarCard car={car} />
              <button onClick={() => removeFav(car.id)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10" data-testid={`unfav-${car.id}`}>
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ===================== SAVED SEARCHES ===================== */
const SavedSearchesSection = ({ token }) => {
  const navigate = useNavigate();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/saved-searches`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSearches(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const deleteSearch = async (id) => {
    await axios.delete(`${API}/saved-searches/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setSearches(prev => prev.filter(s => s.id !== id));
    toast.success('Search deleted');
  };

  const runSearch = (s) => {
    const f = s.filters || {};
    const params = new URLSearchParams();
    if (f.brand) params.set('brand', f.brand);
    if (f.body_type) params.set('body_type', f.body_type);
    if (f.search) params.set('search', f.search);
    if (f.price_min) params.set('price_min', f.price_min);
    if (f.price_max) params.set('price_max', f.price_max);
    navigate(`/cars?${params.toString()}`);
  };

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-5">Saved Searches</h1>
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />)}</div>
      ) : searches.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-white/[0.02] border border-white/5">
          <Search className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 text-sm mb-2">No saved searches</p>
          <p className="text-gray-600 text-xs">Save a search from the Cars page to get notified of new matches</p>
        </div>
      ) : (
        <div className="space-y-2">
          {searches.map(s => (
            <div key={s.id} className="rounded-xl p-4 bg-white/[0.02] border border-white/5 flex items-center gap-3 hover:border-white/10 transition-colors" data-testid={`saved-search-${s.id}`}>
              <div className="w-9 h-9 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0">
                <Search className="w-4 h-4 text-cyan-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{s.name}</p>
                <p className="text-[10px] text-gray-500 truncate">
                  {Object.entries(s.filters || {}).filter(([,v]) => v).map(([k,v]) => `${k}: ${v}`).join(' | ') || 'All cars'}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => runSearch(s)} className="text-xs h-7 border-white/10 shrink-0">Run</Button>
              <button onClick={() => deleteSearch(s.id)} className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-red-400 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ===================== MESSAGES ===================== */
const MessagesSection = ({ token, user }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const backendBase = API.replace('/api', '');

  useEffect(() => {
    axios.get(`${API}/conversations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setConversations(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const openConv = async (conv) => {
    setActiveConv(conv);
    try {
      const r = await axios.get(`${API}/conversations/${conv.id}/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(r.data);
      // Mark read in local state
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread: { ...c.unread, [user.id]: 0 } } : c));
    } catch { }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMsg = async () => {
    if (!msgText.trim() || !activeConv) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/conversations/${activeConv.id}/messages`, { content: msgText }, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(prev => [...prev, r.data]);
      setMsgText('');
      setConversations(prev => prev.map(c => c.id === activeConv.id ? { ...c, last_message: msgText, last_message_at: new Date().toISOString() } : c));
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const getOtherName = (conv) => {
    const otherId = conv.participants.find(p => p !== user.id);
    return conv.participant_names?.[otherId] || 'User';
  };
  const getOtherAvatar = (conv) => {
    const otherId = conv.participants.find(p => p !== user.id);
    return conv.participant_avatars?.[otherId] || '';
  };
  const getUnread = (conv) => conv.unread?.[user.id] || 0;

  // Conversation list view
  if (!activeConv) {
    return (
      <div>
        <h1 className="font-heading text-2xl font-bold mb-5">Messages</h1>
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />)}</div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-16 rounded-2xl bg-white/[0.02] border border-white/5">
            <MessageCircle className="w-12 h-12 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500 text-sm mb-2">No messages yet</p>
            <p className="text-gray-600 text-xs">Start a conversation by contacting a seller on any car listing</p>
          </div>
        ) : (
          <div className="space-y-1">
            {conversations.map(conv => {
              const unread = getUnread(conv);
              return (
                <button key={conv.id} onClick={() => openConv(conv)} className={`w-full rounded-xl p-3 flex items-center gap-3 text-left transition-colors hover:bg-white/[0.04] ${unread > 0 ? 'bg-cyan-500/5 border border-cyan-500/10' : 'bg-white/[0.02] border border-white/5'}`} data-testid={`conv-${conv.id}`}>
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center overflow-hidden shrink-0">
                    {getOtherAvatar(conv) ? <img src={`${backendBase}${getOtherAvatar(conv)}`} alt="" className="w-full h-full object-cover" /> : <User className="w-4 h-4 text-cyan-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold truncate">{getOtherName(conv)}</p>
                      <span className="text-[9px] text-gray-600 shrink-0">{new Date(conv.last_message_at).toLocaleDateString()}</span>
                    </div>
                    {conv.car_title && <p className="text-[10px] text-cyan-500/70 truncate">{conv.car_title}</p>}
                    <p className="text-xs text-gray-500 truncate">{conv.last_message || 'No messages yet'}</p>
                  </div>
                  {unread > 0 && <div className="w-5 h-5 rounded-full bg-cyan-500 flex items-center justify-center shrink-0"><span className="text-[9px] text-black font-bold">{unread}</span></div>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Active conversation view
  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      {/* Chat Header */}
      <div className="flex items-center gap-3 pb-3 border-b border-white/5 shrink-0">
        <button onClick={() => setActiveConv(null)} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10"><ChevronLeft className="w-4 h-4" /></button>
        <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center overflow-hidden">
          {getOtherAvatar(activeConv) ? <img src={`${backendBase}${getOtherAvatar(activeConv)}`} alt="" className="w-full h-full object-cover" /> : <User className="w-3.5 h-3.5 text-cyan-500" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate">{getOtherName(activeConv)}</p>
          {activeConv.car_title && <p className="text-[10px] text-cyan-500/70 truncate">Re: {activeConv.car_title}</p>}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-3">
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[70%] rounded-xl px-3.5 py-2.5 ${msg.sender_id === user.id ? 'bg-cyan-500/15 text-white' : 'bg-white/5 text-gray-300'}`}>
              <p className="text-sm">{msg.content}</p>
              <p className="text-[9px] text-gray-600 mt-1">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex gap-2 pt-3 border-t border-white/5 shrink-0">
        <Input value={msgText} onChange={e => setMsgText(e.target.value)} placeholder="Type a message..." className="h-10 text-sm bg-black/30 border-white/10" onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMsg()} data-testid="msg-input" />
        <Button onClick={sendMsg} disabled={sending || !msgText.trim()} className="bg-cyan-500 text-black hover:bg-cyan-400 h-10 px-4 shrink-0" data-testid="msg-send-btn">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

/* ===================== EDIT CAR ===================== */
const EditCarSection = ({ token, carId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef(null);
  const backendBase = API.replace('/api', '');

  const [form, setForm] = useState({
    title: '', brand: '', model: '', year: '', price: '', mileage: '',
    fuel_type: '', transmission: '', body_type: '', color: '', description: '',
    location: '', condition: 'used', engine: '', trim: '', vin: '', status: 'active',
  });

  useEffect(() => {
    axios.get(`${API}/cars/${carId}`).then(r => {
      const c = r.data;
      setForm({
        title: c.title || '', brand: c.brand || '', model: c.model || '',
        year: c.year || '', price: c.price || '', mileage: c.mileage || '',
        fuel_type: c.fuel_type || '', transmission: c.transmission || '',
        body_type: c.body_type || '', color: c.color || '',
        description: c.description || '', location: c.location || '',
        condition: c.condition || 'used', engine: c.engine || '',
        trim: c.trim || '', vin: c.vin || '', status: c.status || 'active',
      });
      setImages((c.images || []).map((url, i) => ({ url, name: `Photo ${i + 1}` })));
    }).catch(() => toast.error('Car not found'))
    .finally(() => setLoading(false));
  }, [carId]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const newImages = [...images];
    for (const file of files) {
      if (newImages.length >= 10) break;
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
        newImages.push({ url: r.data.url, name: file.name });
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setImages(newImages);
    setUploading(false);
    if (imgRef.current) imgRef.current.value = '';
  };

  const removeImage = (idx) => setImages(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.brand.trim()) { toast.error('Brand is required'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/cars/${carId}`, {
        title: form.title.trim(), brand: form.brand.trim(), model: form.model.trim(),
        year: parseInt(form.year) || 2024, price: parseFloat(form.price) || 0,
        mileage: parseInt(form.mileage) || 0, fuel_type: form.fuel_type,
        transmission: form.transmission, body_type: form.body_type,
        color: form.color, description: form.description,
        location: form.location, condition: form.condition,
        images: images.map(img => img.url), vin: form.vin,
        engine: form.engine, trim: form.trim, status: form.status,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Listing updated!');
      navigate('/dashboard/cars');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-heading text-2xl font-bold">Edit Listing</h1>
        <div className="flex gap-2">
          <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="h-8 px-3 text-xs bg-black/30 border border-white/10 rounded-lg text-gray-300">
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="sold">Sold</option>
          </select>
        </div>
      </div>

      {/* Photos */}
      <div className="rounded-xl p-4 bg-white/[0.02] border border-white/5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <label className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Photos ({images.length}/10)</label>
          <Button variant="outline" size="sm" onClick={() => imgRef.current?.click()} disabled={uploading || images.length >= 10} className="text-xs h-7 border-white/10">
            {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
            {uploading ? 'Uploading...' : 'Add Photos'}
          </Button>
          <input ref={imgRef} type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
        </div>
        {images.length === 0 ? (
          <div className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center cursor-pointer hover:border-cyan-500/30" onClick={() => imgRef.current?.click()}>
            <Camera className="w-8 h-8 text-gray-600 mx-auto mb-2" />
            <p className="text-xs text-gray-400">Click to add car photos</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {images.map((img, i) => (
              <div key={i} className="relative group aspect-[4/3] rounded-lg overflow-hidden bg-white/5">
                <img src={img.url.startsWith('/api/') ? `${backendBase}${img.url}` : img.url} alt="" className="w-full h-full object-cover" />
                <button onClick={() => removeImage(i)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3 text-white" /></button>
                {i === 0 && <span className="absolute bottom-1 left-1 text-[8px] px-1.5 py-0.5 bg-cyan-500 text-black rounded font-bold">Cover</span>}
              </div>
            ))}
            {images.length < 10 && (
              <div className="aspect-[4/3] rounded-lg border-2 border-dashed border-white/10 flex items-center justify-center cursor-pointer hover:border-cyan-500/30" onClick={() => imgRef.current?.click()}>
                <Plus className="w-5 h-5 text-gray-600" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Form */}
      <div className="rounded-xl p-4 bg-white/[0.02] border border-white/5 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="text-[10px] text-gray-500 mb-1 block uppercase tracking-wider">Title *</label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="h-9 text-sm bg-black/30 border-white/10" />
          </div>
          {[
            { key: 'brand', label: 'Brand *' }, { key: 'model', label: 'Model *' },
            { key: 'year', label: 'Year', type: 'number' }, { key: 'body_type', label: 'Body Type' },
            { key: 'fuel_type', label: 'Fuel Type' }, { key: 'transmission', label: 'Transmission' },
            { key: 'engine', label: 'Engine' }, { key: 'color', label: 'Color' },
            { key: 'trim', label: 'Trim' }, { key: 'condition', label: 'Condition' },
            { key: 'price', label: 'Price (EUR) *', type: 'number' }, { key: 'mileage', label: 'Mileage (km)', type: 'number' },
            { key: 'location', label: 'Location' }, { key: 'vin', label: 'VIN Code' },
          ].map(f => (
            <div key={f.key}>
              <label className="text-[10px] text-gray-500 mb-1 block uppercase tracking-wider">{f.label}</label>
              <Input value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} type={f.type || 'text'} className="h-9 text-sm bg-black/30 border-white/10" />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="text-[10px] text-gray-500 mb-1 block uppercase tracking-wider">Description</label>
            <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={4} className="text-sm bg-black/30 border-white/10" />
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <Button onClick={handleSave} disabled={saving} className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold h-10 px-6">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />} Save Changes
          </Button>
          <Button variant="outline" onClick={() => navigate('/dashboard/cars')} className="h-10 px-4 border-white/10">Cancel</Button>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
